import siteConfig from "site-config";
import { Observable } from "rxjs";
import { ajax } from "lib/observable";
import { GLOBAL } from "constants/action-types";
import { add as snackbarAdd } from "actions/snackbars";
import { formatMessage } from "lib/globalize-util";
import { fromJS } from "immutable";
import me from "models/me";
import { Item } from "constants/product-collection";
import { get, chunk } from "lodash";
import { getVariantColorIds } from "helpers/product-collection";

const PAGE_SIZE = 20;
const getProductsAndVariantActions = ({
    data,
    scroll_id = "",
    meta,
    variants = false,
    modal = false,
    merchantId = "",
    brandId = "",
}) => {
    let variantColorIds: Array<{ string_value: string }>;
    const products = data.map((entry, index) => {
        if (index === 0 && variants && entry) {
            const variantColors = get(entry.snapshot.root.mutable_map, Item.FIELDS.VARIANT_COLORS);
            variantColorIds = getVariantColorIds(variantColors);
        }
        return fromJS(entry.snapshot.root.mutable_map);
    });
    let next;
    if (scroll_id) {
        next = {
            ...meta?.request,
            scroll_id,
        };
    }

    const variantActions = [];
    if (variantColorIds) {
        const variantChunks = chunk(variantColorIds, PAGE_SIZE);
        variantChunks.forEach((variantChunk) => {
            variantActions.push({
                type: GLOBAL.PRODUCTS.VARIANTS.FETCH.START,
                payload: {
                    merchantId,
                    brandId,
                    variantColorIds: variantChunk,
                    modal,
                },
            });
        });
    }

    return [products, variantActions, next];
};

const registerNuAnalytics = ({ search, routing, scope }) => {
    if (search && search.query) {
        const eventParams = {
            c: "product",
            a: "search",
        };
        const customDimensions = {
            term: search.query,
            search_origin_page: routing.locationBeforeTransitions.pathname,
            brand_id: scope && scope.brand && scope.brand._id,
        };
        me.nuAnalytics("event", [eventParams, customDimensions]);
    }
};

export const fetchGlobalProductsEpic = (action$, store) => {
    return action$
        .ofType(GLOBAL.PRODUCTS.FETCH.START)
        .map(({ payload }) => payload)
        .flatMap(({ search, filter, limit = PAGE_SIZE, modal, variants = false, merchantId, productId }) => {
            const { scope, routing, entities } = store.getState();
            let filters = [
                {
                    eq: {
                        field: "_archived",
                        value: {
                            string_value: "false",
                        },
                    },
                },
                {
                    eq: {
                        field: "_active",
                        value: {
                            string_value: "true",
                        },
                    },
                },
            ];

            if (me.isBrandUser()) {
                const brandFilter = {
                    in: {
                        field: "_brand_id",
                        values: [{ string_value: scope?.brand?._id }],
                    },
                };
                filters.push(brandFilter as any);
            }

            if (!Array.isArray(filter) && filter) {
                filters.push(filter);
            } else if (Array.isArray(filter) && filter.length > 0) {
                filters = filters.concat(filter);
            }

            const params: any = {
                method: "POST",
                url: `${siteConfig.api}/api/v3.0/query/search_products`,
                body: {
                    limit,
                    with_scroll: true,
                    seeker_merchant_id: merchantId,
                    filter: {
                        and: {
                            filters,
                        },
                    },
                },
            };

            if (search && search.query) {
                params.body.search = {
                    ...search,
                    query: `\"${search.query}\"`,
                };
            }

            const brandScopeClause = filters.find(
                (filterObj: any) => filterObj.in && filterObj.in.field === "_brand_id"
            );
            let brandId;
            if (brandScopeClause) {
                brandId = (brandScopeClause as any).in.values[0].string_value;
            }

            const items = entities.items;

            if (productId && items) {
                // Search for Product snapshot
                const item = items[productId];

                if (item && item.product) {
                    const [products, variantActions, next] = getProductsAndVariantActions({
                        // Use existing Product that was gotten by collection_fetch_products_by_ids
                        data: [item.product],
                        meta: undefined,
                        variants,
                        modal,
                        merchantId,
                        brandId,
                    });
                    registerNuAnalytics({ search, routing, scope });

                    return [
                        {
                            payload: {
                                results: products,
                                nextPageQuery: next,
                                modal,
                                merchantId,
                                brandId,
                            },
                            meta: { modal },
                            type: GLOBAL.PRODUCTS.FETCH.COMPLETED,
                        },
                        ...variantActions,
                    ] as any;
                }
            }

            return ajax(params)
                .map(({ response }) => response)
                .flatMap(({ data, scroll_id, meta }) => {
                    const [products, variantActions, next] = getProductsAndVariantActions({
                        data,
                        scroll_id,
                        meta,
                        variants,
                        modal,
                        merchantId,
                        brandId,
                    });
                    registerNuAnalytics({ search, routing, scope });

                    return [
                        {
                            payload: {
                                results: products,
                                nextPageQuery: next,
                                modal,
                                merchantId,
                                brandId,
                            },
                            meta: { modal },
                            type: GLOBAL.PRODUCTS.FETCH.COMPLETED,
                        },
                        ...variantActions,
                    ] as any;
                })
                .catch((error) =>
                    Observable.merge(
                        Observable.of({
                            payload: {
                                error: new Error(error),
                                modal,
                            },
                            error: true,
                            type: GLOBAL.PRODUCTS.FETCH.COMPLETED,
                        }),
                        Observable.of(snackbarAdd({ message: formatMessage("Products not found") }))
                    )
                );
        });
};

export const fetchGlobalProductsVariantEpic = (action$, store) => {
    return action$
        .ofType(GLOBAL.PRODUCTS.VARIANTS.FETCH.START)
        .map(({ payload }) => payload)
        .flatMap(({ brandId, merchantId, variantColorIds, modal }) => {
            const filters = [
                {
                    eq: {
                        field: "_archived",
                        value: {
                            string_value: "false",
                        },
                    },
                },
                {
                    eq: {
                        field: "_active",
                        value: {
                            string_value: "true",
                        },
                    },
                },
                {
                    in: {
                        field: "_product_id",
                        values: variantColorIds,
                    },
                },
            ];

            if (brandId) {
                filters.push({
                    in: {
                        field: "_brand_id",
                        values: [
                            {
                                string_value: brandId,
                            },
                        ],
                    },
                });
            }

            const params = {
                method: "POST",
                url: `${siteConfig.api}/api/v3.0/query/search_products`,
                body: {
                    limit: PAGE_SIZE,
                    with_scroll: true,
                    filter: {
                        and: {
                            filters,
                        },
                    },
                    seeker_merchant_id: merchantId,
                },
            };

            return ajax(params)
                .map(({ response }) => response)
                .flatMap(({ data }) => {
                    const [products] = getProductsAndVariantActions({
                        data,
                        meta: undefined,
                    });

                    return [
                        {
                            payload: {
                                results: products,
                                modal,
                            },
                            type: GLOBAL.PRODUCTS.VARIANTS.FETCH.COMPLETED,
                        },
                    ];
                })
                .catch((error) =>
                    Observable.merge(
                        Observable.of({
                            payload: {
                                error: new Error(error),
                                modal,
                            },
                            error: true,
                            type: GLOBAL.PRODUCTS.VARIANTS.FETCH.COMPLETED,
                        }),
                        Observable.of(snackbarAdd({ message: formatMessage("Variants not found") }))
                    )
                );
        });
};

export const fetchNextGlobalProductsEpic = (action$, store) => {
    return action$
        .ofType(GLOBAL.PRODUCTS.FETCH.NEXT)
        .map(({ payload }) => payload)
        .switchMap(({ modal = false, merchantId = "" }) => {
            const { nextPageQuery, nextPageQueryModal } = store.getState().globalProducts;
            const params = {
                method: "POST",
                url: `${siteConfig.api}/api/v3.0/query/search_products`,
                body: modal
                    ? { ...nextPageQueryModal, seeker_merchant_id: merchantId }
                    : { ...nextPageQuery, seeker_merchant_id: merchantId },
            };
            return ajax(params)
                .map(({ response }) => response)
                .flatMap(({ data, scroll_id, meta }) => {
                    const [products, next] = getProductsAndVariantActions({
                        data,
                        scroll_id,
                        meta,
                    });

                    return [
                        {
                            payload: {
                                results: products,
                                nextPageQuery: next,
                                modal,
                            },
                            type: GLOBAL.PRODUCTS.FETCH.COMPLETED,
                        },
                    ];
                })
                .catch((error) =>
                    Observable.merge(
                        Observable.of({
                            payload: new Error(error),
                            error: true,
                            type: GLOBAL.PRODUCTS.FETCH.COMPLETED,
                        }),
                        Observable.of(snackbarAdd({ message: formatMessage("Products not found") }))
                    )
                );
        });
};
